import React, { useContext, useMemo, useRef, useState } from "react";
import { CodeEditorSurface } from "@/components/Form/CodeEditor/CodeEditor";
import { SpotToast } from "@/components/SpotToast";
import { BindableInput } from "@/components/SpotflowV2/Sidebar/BindableInput";
import { useUpstreamVariableOptions } from "@/components/SpotflowV2/Sidebar/InlineVariablePicker";
import { nodeOptionsToEditorInputs } from "@/components/SpotflowV2/Sidebar/editorInputsFromOptions";
import { ComplexMarkdownEditor } from "@/components/Form/ComplexMarkdownField/ComplexMarkdownField";
import { ComplexMarkdownDialog } from "@/components/Form/ComplexMarkdownField/ComplexMarkdownDialog";
import type { EditorInput } from "@/components/Form/ComplexMarkdownField/types";
import {
  formatTemplateToken,
  normalizeVariableRefId,
  templateVariableLabel,
  WHOLE_VAR_REF_PATTERN,
} from "@/components/SpotflowV2/Sidebar/variableRefs";
import { normalizeDataType } from "@/components/SpotflowV2/Sidebar/typeCompatibility";
import { MilkdownProvider } from "@milkdown/react";
import { PlaybookEditorContext } from "./playbookEditorContext";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/shadcn/card";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import { Switch } from "@/components/shadcn/switch";
import { Textarea } from "@/components/shadcn/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Trash2,
  MousePointer,
  Type,
  Camera,
  Clock,
  Navigation,
  Bot,
  FileDown,
  FileUp,
  Plus,
  RefreshCw,
  Keyboard,
  Download,
  Cookie,
  Eraser,
  ArrowDown,
  Code,
  Globe,
  AlertTriangle,
  Eye,
  EyeOff,
  ListChecks,
  ArrowLeftRight,
  StopCircle,
  Terminal,
  X,
} from "lucide-react";
import { Badge } from "@/components/shadcn/badge";
import { cn } from "@/lib/utils";
import { AddStepModal } from "./AddStepModal";
import { SelectorEditor } from "./SelectorEditor";
import { collectStepOutputNames } from "./stepOutputs";

interface StepCardProps {
  id: string;
  index: number;
  step: any;
  onUpdate: (updates: any) => void;
  onDelete: () => void;
  onReplace?: (newStep: any) => void;
  inputVariableNames?: string[];
  insideLoop?: boolean;
}

const ACTION_ICONS: Record<string, any> = {
  navigate: Globe,
  reload: RefreshCw,
  switch_tab: ArrowLeftRight,
  click: MousePointer,
  type: Type,
  fill: Type,
  select: ListChecks,
  press: Keyboard,
  upload: FileUp,
  extract: Download,
  screenshot: Camera,
  download: FileDown,
  extract_cookies: Cookie,
  extract_request_headers: Download,
  extract_url: Globe,
  set_cookies: Cookie,
  clear_cookies: Eraser,
  wait_for: Clock,
  wait_for_timeout: Clock,
  wait_for_load: Clock,
  scroll: ArrowDown,
  if: Code,
  loop: Code,
  break: StopCircle,
  set_variable: FileDown,
  agent: Bot,
  run_script: Terminal,
  placeholder: AlertTriangle,
};

const ACTION_LABELS: Record<string, string> = {
  navigate: "Navigate",
  reload: "Reload",
  switch_tab: "Switch Tab",
  click: "Click",
  type: "Type",
  fill: "Fill",
  select: "Select Option",
  press: "Press Key",
  upload: "Upload File",
  extract: "Extract",
  screenshot: "Screenshot",
  download: "Download",
  extract_cookies: "Get Cookies",
  extract_request_headers: "Extract Request Headers",
  extract_url: "Extract Current URL",
  set_cookies: "Set Cookies",
  clear_cookies: "Clear Cookies",
  wait_for: "Wait For",
  wait_for_timeout: "Wait",
  wait_for_load: "Wait for Page Load",
  scroll: "Scroll",
  if: "If Condition",
  loop: "Loop",
  break: "Break Loop",
  set_variable: "Log Value",
  agent: "AI Agent",
  run_script: "Run Script",
  placeholder: "Setup",
};

function defaultTimeoutSeconds(stepType: string): string {
  if (stepType === "agent") return "60";
  if (stepType === "run_script") return "300";
  return "30";
}

export function StepCard({
  id,
  index,
  step,
  onUpdate,
  onDelete,
  onReplace,
  inputVariableNames = [],
  insideLoop = false,
}: StepCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const isPlaceholder = step.type === "placeholder";
  const needsPassword = step.sensitive && !step.value;
  const isWarning = isPlaceholder || needsPassword;

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging && {
      opacity: 0.8,
      zIndex: 50,
      position: "relative" as const,
    }),
  };

  const Icon = ACTION_ICONS[step.type] || MousePointer;
  const label = ACTION_LABELS[step.type] || step.type;

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        className={cn(
          "overflow-hidden p-0 bg-muted/30 gap-0",
          isWarning &&
            "border-warning-400 dark:border-warning-600 border-dashed"
        )}
      >
        {/* Header */}
        <div
          className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing hover:text-primary transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 hover:bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>

          <Icon
            className={cn(
              "h-4 w-4 flex-shrink-0",
              isWarning ? "text-warning-500" : "text-primary"
            )}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-muted-foreground">
                #{index + 1}
              </span>
              <span className="font-medium">{label}</span>
              {step.description && (
                <span className="text-sm text-muted-foreground truncate">
                  - {step.description}
                </span>
              )}
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Expanded Content */}
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-in-out"
          style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="px-4 pb-4 pt-4 space-y-4 [&_input]:bg-background [&_textarea]:bg-background [&_[data-slot=select-trigger]]:bg-background">
              {/* Description — hidden for placeholder steps */}
              {!isPlaceholder && (
                <div className="flex flex-col gap-1.5">
                  <Label>Description</Label>
                  <Input
                    value={step.description || ""}
                    onChange={(e) => onUpdate({ description: e.target.value })}
                    placeholder="What does this step do?"
                  />
                </div>
              )}

              {/* Action-specific fields */}
              {renderActionFields(
                step,
                onUpdate,
                inputVariableNames,
                insideLoop
              )}

              {/* Replace button for placeholder steps */}
              {isPlaceholder && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full border-warning-400 dark:border-warning-600 text-warning-700 dark:text-warning-300 hover:bg-warning-50 dark:hover:bg-warning-950/30"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsReplaceModalOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Replace Step
                </Button>
              )}

              {/* Advanced Settings — hidden for placeholder steps */}
              {!isPlaceholder && (
                <div className="border-t pt-4">
                  <Button
                    variant="ghost"
                    className="h-9 px-2 text-sm font-medium w-full justify-start hover:bg-muted/50"
                    onClick={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
                  >
                    {isAdvancedExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    Advanced Settings
                  </Button>

                  {isAdvancedExpanded && (
                    <div className="space-y-4 mt-3 p-4 bg-background rounded-lg border">
                      {/* Timeout */}
                      <div className="flex flex-col gap-1.5">
                        <Label>Timeout (seconds)</Label>
                        <p className="text-xs text-muted-foreground mb-1.5">
                          Defaults to {defaultTimeoutSeconds(step.type)}s if not
                          specified
                        </p>
                        <Input
                          type="number"
                          value={
                            step.timeout != null
                              ? Math.round(step.timeout / 1000)
                              : ""
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            onUpdate({
                              timeout: val ? parseInt(val) * 1000 : null,
                            });
                          }}
                          placeholder={defaultTimeoutSeconds(step.type)}
                          className="text-sm h-9"
                        />
                      </div>

                      {/* Continue on Error */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex flex-col gap-1.5">
                          <Label>Continue on error</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Don&apos;t stop the playbook if this step fails
                          </p>
                        </div>
                        <Switch
                          checked={step.continue_on_error ?? false}
                          onCheckedChange={(checked) =>
                            onUpdate({ continue_on_error: checked })
                          }
                        />
                      </div>

                      {/* Agent Fallback - not shown for agent/break/script steps */}
                      {step.type !== "agent" &&
                        step.type !== "break" &&
                        step.type !== "run_script" && (
                          <>
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex flex-col gap-1.5">
                                <Label>Agent fallback</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  If this step fails, retry it using an AI agent
                                </p>
                              </div>
                              <Switch
                                checked={step.agent_fallback ?? false}
                                onCheckedChange={(checked) =>
                                  onUpdate({ agent_fallback: checked })
                                }
                              />
                            </div>
                            {step.agent_fallback && (
                              <div className="flex flex-col gap-1.5">
                                <Label>Description (for AI repair)</Label>
                                <Textarea
                                  rows={3}
                                  value={step.selector?.description || ""}
                                  onChange={(e) =>
                                    onUpdate({
                                      selector: {
                                        ...(step.selector || {
                                          primary: "",
                                          fallbacks: [],
                                        }),
                                        description: e.target.value,
                                      },
                                    })
                                  }
                                  placeholder="e.g., 'The submit button at the bottom of the login form'"
                                />
                              </div>
                            )}
                          </>
                        )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
      {isPlaceholder && (
        <AddStepModal
          open={isReplaceModalOpen}
          onClose={() => setIsReplaceModalOpen(false)}
          onAdd={(newStep: any) => {
            onReplace?.({ ...newStep, id });
            setIsReplaceModalOpen(false);
          }}
          insideLoop={insideLoop}
        />
      )}
    </>
  );
}

/** Step text field. V2 editor: the same components other V2 nodes use —
 * `BindableInput` for single-line values (literal or one whole-field
 * `:var[...]` chip, like the Conditional node's Value field) and
 * `ComplexMarkdownEditor` for the multiline prompt (inline `:var[...]` chips,
 * like Generate Text). V1: Tiptap with `{{var}}` chips from the node's
 * declared input variables. */
export function VariableInput({
  value,
  onChange,
  placeholder,
  variableNames,
  multiline,
  dataType = "str",
  min,
}: {
  value: string;
  onChange: (text: string) => void;
  placeholder?: string;
  variableNames: string[];
  multiline?: boolean;
  /** V2 bindable type: "number" renders a numeric literal input and offers
   * only number variables in the picker. */
  dataType?: "str" | "number";
  /** Number fields only: floor enforced by the numeric input (stepper stops
   * there, typed values clamp on blur). */
  min?: number;
}) {
  const { v2NodeId } = useContext(PlaybookEditorContext);
  const upstreamOptions = useUpstreamVariableOptions({ inlineText: true });
  const [isExpanded, setIsExpanded] = useState(false);
  // Intra-playbook variables (loop_item, loop_index, prior step outputs) are
  // {{...}} templates the executor substitutes at run time; they join the
  // picker as a "Playbook" group and always serialize as the literal
  // {{...}} token, never a :var ref.
  const templateInputs = useMemo<EditorInput[]>(
    () =>
      variableNames.map((name) => ({
        id: formatTemplateToken(name),
        name: templateVariableLabel(name),
        dataType: "str",
        isList: false,
        sourceName: "Playbook",
        sourceColor: "#e0e7ff",
        sourceKind: "template" as const,
      })),
    [variableNames]
  );
  const editorInputs = useMemo(
    () => [...nodeOptionsToEditorInputs(upstreamOptions), ...templateInputs],
    [upstreamOptions, templateInputs]
  );

  if (v2NodeId) {
    if (multiline) {
      return (
        <>
          <MilkdownProvider>
            <ComplexMarkdownEditor
              value={value || ""}
              onChange={onChange}
              placeholder={placeholder}
              nodeId={v2NodeId}
              inputs={editorInputs}
              templateChips
              onExpand={() => setIsExpanded(true)}
            />
          </MilkdownProvider>
          <ComplexMarkdownDialog
            isOpen={isExpanded}
            onOpenChange={setIsExpanded}
            initialValue={value || ""}
            onCommit={onChange}
            inputs={editorInputs}
            placeholder={placeholder}
            templateChips
          />
        </>
      );
    }
    return (
      <BindableInput
        nodeId={v2NodeId}
        dataType={dataType}
        value={value || ""}
        placeholder={placeholder}
        onChange={onChange}
        templateVariables={variableNames}
        integerOnly={dataType === "number"}
        min={min}
        clearsToEmpty={dataType === "number"}
      />
    );
  }

  // When no variables are available, use a plain Input for compact sizing
  if (variableNames.length === 0 && !multiline) {
    return (
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    );
  }

  const TiptapEditor =
    require("@/components/Form/TiptapEditor/TiptapEditor").default;
  const {
    injectMentionsIntoDoc,
  } = require("@/components/Form/TiptapEditor/chipUtils");

  // Convert {{var}} ↔ ((var))
  const toTiptap = (s: string) => s.replace(/\{\{([^}]+)\}\}/g, "(($1))");
  const fromTiptap = (s: string) => s.replace(/\(\(([^)]+)\)\)/g, "{{$1}}");

  const chipOptions = [
    {
      label: "Variables",
      // The id IS the variable name — the executor substitutes by name.
      values: variableNames.map((n: string) => ({ value: n, label: n })),
    },
  ];

  // Build tiptap doc with mention nodes from ((var)) tokens
  const tiptapText = toTiptap(value || "");
  const plainDoc = {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: tiptapText ? [{ type: "text", text: tiptapText }] : [],
      },
    ],
  };
  const definition = injectMentionsIntoDoc(plainDoc, chipOptions);

  const richValue = {
    text: tiptapText,
    definition,
  };

  return (
    <TiptapEditor
      value={richValue}
      onChange={(v: any) => onChange(fromTiptap(v.text || ""))}
      chipOptions={chipOptions}
      placeholder={placeholder}
      isSimpleText={!multiline}
      config={{
        hideToolbar: true,
        numberLines: multiline ? 3 : 1,
        hideChipOptions: true,
      }}
    />
  );
}

/** Upload step's file source. V2: bind an upstream File output (whole-field
 * `:var[...]`) or type a previous download step's variable name. V1: a plain
 * name input with the node's declared File variables as suggestions. */
function UploadFileField({
  value,
  onChange,
  inputVariableNames,
}: {
  value: string;
  onChange: (next: string) => void;
  inputVariableNames: string[];
}) {
  const { v2NodeId } = useContext(PlaybookEditorContext);
  if (v2NodeId) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label>File</Label>
        <BindableInput
          nodeId={v2NodeId}
          dataType="File"
          value={typeof value === "string" ? value : ""}
          placeholder="my_file"
          onChange={onChange}
        />
        <p className="text-xs text-muted-foreground">
          Bind an upstream File variable, or type the name of a variable from a
          previous download step.
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <Label>File Variable</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="my_file"
        list="playbook-file-variables"
      />
      {inputVariableNames && inputVariableNames.length > 0 && (
        <datalist id="playbook-file-variables">
          {inputVariableNames.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      )}
      <p className="text-xs text-muted-foreground">
        Name a new variable to expose it as a File input on the node, or
        reference a variable from a previous download step.
      </p>
    </div>
  );
}

function renderActionFields(
  step: any,
  onUpdate: (updates: any) => void,
  inputVariableNames: string[] = [],
  insideLoop = false
) {
  switch (step.type) {
    case "navigate":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>URL</Label>
            <VariableInput
              value={step.url || ""}
              onChange={(url) => onUpdate({ url })}
              placeholder="https://example.com"
              variableNames={inputVariableNames}
            />
          </div>
        </>
      );

    case "reload":
      return null;

    case "switch_tab":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Switch to</Label>
            <Select
              value={step.by || "latest"}
              onValueChange={(v) => onUpdate({ by: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Latest opened tab</SelectItem>
                <SelectItem value="index">By index</SelectItem>
                <SelectItem value="url">By URL</SelectItem>
                <SelectItem value="title">By title</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {step.by === "index" && (
            <div className="flex flex-col gap-1.5">
              <Label>Tab index</Label>
              <VariableInput
                value={step.value == null ? "" : String(step.value)}
                onChange={(raw) => {
                  // Integer input is stored as a number; anything else (a
                  // :var[...] ref or {{template}}) stays a string —
                  // SwitchTabAction.value accepts `str | int | None`.
                  if (/^-?\d+$/.test(raw)) {
                    onUpdate({ value: parseInt(raw, 10) });
                  } else {
                    onUpdate({ value: raw });
                  }
                }}
                placeholder="0 (first tab); -1 for last"
                variableNames={inputVariableNames}
                dataType="number"
              />
            </div>
          )}
          {(step.by === "url" || step.by === "title") && (
            <>
              <div className="flex flex-col gap-1.5">
                <Label>{step.by === "url" ? "URL" : "Title"} to match</Label>
                <VariableInput
                  value={step.value || ""}
                  onChange={(value) => onUpdate({ value })}
                  placeholder={
                    step.by === "url" ? "example.com/page" : "Tab title"
                  }
                  variableNames={inputVariableNames}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Match</Label>
                <Select
                  value={step.match || "contains"}
                  onValueChange={(v) => onUpdate({ match: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contains">Contains</SelectItem>
                    <SelectItem value="exact">Exact</SelectItem>
                    <SelectItem value="regex">Regex</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </>
      );

    case "click":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Element Selector</Label>
            <SelectorEditor
              selector={step.selector || { primary: "", fallbacks: [] }}
              onChange={(selector) => onUpdate({ selector })}
              variableNames={inputVariableNames}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>Opens a new tab</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Wait for a new tab to open after this click and switch to it
              </p>
            </div>
            <Switch
              checked={step.expect_new_tab ?? false}
              onCheckedChange={(checked) =>
                onUpdate({ expect_new_tab: checked })
              }
            />
          </div>
        </>
      );

    case "type":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Element Selector</Label>
            <SelectorEditor
              selector={step.selector || { primary: "", fallbacks: [] }}
              onChange={(selector) => onUpdate({ selector })}
              variableNames={inputVariableNames}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Text to Type</Label>
            <VariableInput
              value={step.text || ""}
              onChange={(text) => onUpdate({ text })}
              placeholder="Text to type"
              variableNames={inputVariableNames}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label>Clear existing text first</Label>
            <Switch
              checked={step.clear_first || false}
              onCheckedChange={(checked) => onUpdate({ clear_first: checked })}
            />
          </div>
        </>
      );

    case "fill":
      return (
        <>
          {step.sensitive && !step.value && (
            <div className="rounded-md border border-warning-300 dark:border-warning-700 bg-warning-50 dark:bg-warning-950/30 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-warning-600 dark:text-warning-400 mt-0.5 shrink-0" />
                <p className="text-sm text-warning-800 dark:text-warning-300">
                  This is a password field. The value was not captured during
                  recording. Please enter it manually.
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label>Element Selector</Label>
            <SelectorEditor
              selector={step.selector || { primary: "", fallbacks: [] }}
              onChange={(selector) => onUpdate({ selector })}
              variableNames={inputVariableNames}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Value to Fill</Label>
            {step.sensitive ? (
              <PasswordFillInput
                value={step.value || ""}
                onChange={(value) => onUpdate({ value })}
              />
            ) : (
              <VariableInput
                value={step.value || ""}
                onChange={(value) => onUpdate({ value })}
                placeholder="Value to fill"
                variableNames={inputVariableNames}
              />
            )}
          </div>
        </>
      );

    case "select":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Element Selector</Label>
            <SelectorEditor
              selector={step.selector || { primary: "", fallbacks: [] }}
              onChange={(selector) => onUpdate({ selector })}
              variableNames={inputVariableNames}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Match By</Label>
            <Select
              value={step.by || "value"}
              onValueChange={(by) => onUpdate({ by })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="value">Value attribute</SelectItem>
                <SelectItem value="label">Visible label</SelectItem>
                <SelectItem value="index">Index (0-based)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>
              {step.by === "index"
                ? "Index to select"
                : step.by === "label"
                  ? "Label to select"
                  : "Value to select"}
            </Label>
            <VariableInput
              value={
                Array.isArray(step.value)
                  ? step.value.join(", ")
                  : step.value || ""
              }
              onChange={(raw) => {
                const trimmed = raw.trim();
                const isList = trimmed.includes(",");
                const next = isList
                  ? trimmed
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : raw;
                onUpdate({ value: next });
              }}
              placeholder={
                step.by === "index"
                  ? "0"
                  : step.by === "label"
                    ? "United States"
                    : "us"
              }
              variableNames={inputVariableNames}
            />
            <p className="text-xs text-muted-foreground">
              For multi-select, separate values with commas.
            </p>
          </div>
        </>
      );

    case "press":
      return (
        <PressStepEditor
          step={step}
          onUpdate={onUpdate}
          inputVariableNames={inputVariableNames}
        />
      );

    case "upload":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>File Input Selector</Label>
            <SelectorEditor
              selector={step.selector || { primary: "", fallbacks: [] }}
              onChange={(selector) => onUpdate({ selector })}
              variableNames={inputVariableNames}
            />
          </div>
          <UploadFileField
            value={step.file_variable || ""}
            onChange={(file_variable) => onUpdate({ file_variable })}
            inputVariableNames={inputVariableNames}
          />
        </>
      );

    case "extract":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Output Name</Label>
            <Input
              value={step.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="variable_name"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label>Whole page body</Label>
            <Switch
              checked={step.whole_page || false}
              onCheckedChange={(checked) =>
                onUpdate({
                  whole_page: checked,
                  ...(checked && step.extract_type !== "html"
                    ? { extract_type: "text" }
                    : {}),
                })
              }
            />
          </div>
          {!step.whole_page && (
            <div className="flex flex-col gap-1.5">
              <Label>Element Selector</Label>
              <SelectorEditor
                selector={step.selector || { primary: "", fallbacks: [] }}
                onChange={(selector) => onUpdate({ selector })}
                variableNames={inputVariableNames}
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label>Extract Type</Label>
            <Select
              value={step.extract_type || "text"}
              onValueChange={(v) => onUpdate({ extract_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="html">HTML</SelectItem>
                {!step.whole_page && (
                  <SelectItem value="value">Input Value</SelectItem>
                )}
                {!step.whole_page && (
                  <SelectItem value="attribute">Attribute</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          {!step.whole_page && step.extract_type === "attribute" && (
            <div className="flex flex-col gap-1.5">
              <Label>Attribute Name</Label>
              <Input
                value={step.attribute || ""}
                onChange={(e) => onUpdate({ attribute: e.target.value })}
                placeholder="aria-expanded"
              />
            </div>
          )}
        </>
      );

    case "screenshot":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Output Name</Label>
            <Input
              value={step.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="screenshot_var"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label>Full page screenshot</Label>
            <Switch
              checked={step.full_page || false}
              onCheckedChange={(checked) => onUpdate({ full_page: checked })}
            />
          </div>
        </>
      );

    case "download":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Output Name</Label>
            <Input
              value={step.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="downloaded_file"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Download URL (optional)</Label>
            <VariableInput
              value={step.url || ""}
              onChange={(url) => onUpdate({ url })}
              placeholder="https://example.com/file.pdf"
              variableNames={inputVariableNames}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>OR Click Element to Download</Label>
            {step.trigger_selector && (
              <SelectorEditor
                selector={step.trigger_selector}
                onChange={(selector) =>
                  onUpdate({ trigger_selector: selector })
                }
                variableNames={inputVariableNames}
              />
            )}
            {!step.trigger_selector && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  onUpdate({
                    trigger_selector: {
                      primary: "",
                      fallbacks: [],
                      must_be_visible: true,
                    },
                  })
                }
              >
                Add Download Trigger
              </Button>
            )}
          </div>
        </>
      );

    case "extract_cookies":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Output Name</Label>
            <Input
              value={step.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="cookies"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Domain Filter (optional)</Label>
            <VariableInput
              value={step.domain || ""}
              onChange={(domain) => onUpdate({ domain })}
              placeholder="example.com (leave empty for all domains)"
              variableNames={inputVariableNames}
            />
          </div>
        </>
      );

    case "extract_request_headers":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Output Name</Label>
            <Input
              value={step.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="request_headers"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>
              URL Pattern (optional)
              <span className="ml-2 text-muted-foreground font-normal text-xs">
                - leave empty for last request
              </span>
            </Label>
            <VariableInput
              value={step.url_pattern || ""}
              onChange={(url_pattern) => onUpdate({ url_pattern })}
              placeholder="api.example.com/login (regex or substring)"
              variableNames={inputVariableNames}
            />
          </div>
        </>
      );

    case "extract_url":
      return (
        <div className="flex flex-col gap-1.5">
          <Label>Output Name</Label>
          <Input
            value={step.name || ""}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="current_url"
          />
        </div>
      );

    case "wait_for":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Element Selector</Label>
            <SelectorEditor
              selector={step.selector || { primary: "", fallbacks: [] }}
              onChange={(selector) => onUpdate({ selector })}
              variableNames={inputVariableNames}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Wait State</Label>
            <Select
              value={step.state || "visible"}
              onValueChange={(v) => onUpdate({ state: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="visible">Visible</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
                <SelectItem value="attached">Attached</SelectItem>
                <SelectItem value="detached">Detached</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case "wait_for_timeout":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Timeout (seconds)</Label>
            <Input
              type="number"
              value={Math.round((step.timeout || 1000) / 1000)}
              onChange={(e) => {
                const seconds = parseInt(e.target.value);
                onUpdate({
                  timeout: Number.isNaN(seconds) ? 1000 : seconds * 1000,
                });
              }}
              placeholder="1"
            />
          </div>
        </>
      );

    case "wait_for_load":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Wait Until</Label>
            <Select
              value={step.wait_until || "load"}
              onValueChange={(v) => onUpdate({ wait_until: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="load">Load (full page load)</SelectItem>
                <SelectItem value="domcontentloaded">
                  DOMContentLoaded
                </SelectItem>
                <SelectItem value="networkidle">Network Idle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      );

    case "scroll":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Scroll To</Label>
            <Select
              value={step.to || "top"}
              onValueChange={(v) => onUpdate({ to: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="top">Top</SelectItem>
                <SelectItem value="bottom">Bottom</SelectItem>
                <SelectItem value="element">Element</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {step.to === "element" && (
            <div className="flex flex-col gap-1.5">
              <Label>Element Selector</Label>
              <SelectorEditor
                selector={step.selector || { primary: "", fallbacks: [] }}
                onChange={(selector) => onUpdate({ selector })}
              />
            </div>
          )}
        </>
      );

    case "set_variable":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Output Name</Label>
            <Input
              value={step.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="my_variable"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Value</Label>
            <VariableInput
              value={step.value || ""}
              onChange={(value) => onUpdate({ value })}
              placeholder="Text value (type / to insert variables)"
              variableNames={inputVariableNames}
            />
          </div>
        </>
      );

    case "agent":
      return (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Task Prompt</Label>
            <VariableInput
              value={step.prompt || ""}
              onChange={(prompt) => onUpdate({ prompt })}
              placeholder="Describe what the AI agent should do... (type / to insert variables)"
              variableNames={inputVariableNames}
              multiline
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Output Name</Label>
            <Input
              value={step.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="agent_result"
            />
            <p className="text-xs text-muted-foreground mt-1">
              The agent's response will be stored in this variable
            </p>
          </div>
        </>
      );

    case "run_script":
      return (
        <RunScriptStepEditor
          step={step}
          onUpdate={onUpdate}
          inputVariableNames={inputVariableNames}
        />
      );

    case "if":
      return (
        <IfStepEditor
          step={step}
          onUpdate={onUpdate}
          inputVariableNames={inputVariableNames}
          insideLoop={insideLoop}
        />
      );

    case "loop":
      return (
        <LoopStepEditor
          step={step}
          onUpdate={onUpdate}
          inputVariableNames={inputVariableNames}
        />
      );

    case "break":
      return (
        <BreakStepEditor
          step={step}
          onUpdate={onUpdate}
          inputVariableNames={inputVariableNames}
        />
      );

    case "set_cookies":
      return (
        <SetCookiesEditor
          step={step}
          onUpdate={onUpdate}
          inputVariableNames={inputVariableNames}
        />
      );

    case "clear_cookies":
      return (
        <div className="flex flex-col gap-1.5">
          <Label>Domain (optional)</Label>
          <VariableInput
            value={step.domain || ""}
            onChange={(domain) => onUpdate({ domain })}
            placeholder="example.com (leave empty to clear all cookies)"
            variableNames={inputVariableNames}
          />
        </div>
      );

    case "placeholder":
      return (
        <div className="rounded-md border border-warning-300 dark:border-warning-700 bg-warning-50 dark:bg-warning-950/30 p-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-warning-600 dark:text-warning-400 mt-0.5 shrink-0" />
            <p className="text-sm text-warning-800 dark:text-warning-300">
              This step could not be automatically imported. Click
              &ldquo;Replace Step&rdquo; below to choose the correct action
              type.
            </p>
          </div>
          {step.original_code && (
            <pre className="text-xs font-mono bg-warning-100/50 dark:bg-warning-900/30 p-2 rounded text-warning-900 dark:text-warning-200 overflow-x-auto">
              {step.original_code}
            </pre>
          )}
        </div>
      );

    default:
      return (
        <div className="text-sm text-muted-foreground">
          Configure this action type in JSON mode
        </div>
      );
  }
}

// ── Password Fill Input ───────────────────────────────────────────

function PasswordFillInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter password"
        className="pr-9"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute right-0 top-0 h-full px-2.5 hover:bg-transparent"
        onClick={() => setVisible(!visible)}
      >
        {visible ? (
          <EyeOff className="h-4 w-4 text-muted-foreground" />
        ) : (
          <Eye className="h-4 w-4 text-muted-foreground" />
        )}
      </Button>
    </div>
  );
}

// ── Press Step Editor ──────────────────────────────────────────────

const MODIFIER_KEYS = new Set(["Control", "Shift", "Alt", "Meta"]);

function KeyCaptureInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (key: string) => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [listening, setListening] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Ignore modifier-only presses
    if (MODIFIER_KEYS.has(e.key)) return;

    // Build Playwright key string with modifier prefixes
    const parts: string[] = [];
    if (e.ctrlKey) parts.push("Control");
    if (e.shiftKey) parts.push("Shift");
    if (e.altKey) parts.push("Alt");
    if (e.metaKey) parts.push("Meta");

    const key = e.key === " " ? "Space" : e.key;
    parts.push(key);

    onChange(parts.join("+"));
    setListening(false);
    ref.current?.blur();
  };

  return (
    <div
      ref={ref}
      tabIndex={0}
      role="button"
      className={cn(
        "flex h-9 w-full items-center rounded-md border px-3 py-1 text-sm transition-colors cursor-pointer outline-none",
        listening
          ? "border-primary ring-1 ring-primary bg-background"
          : "border-input bg-background hover:bg-accent/50"
      )}
      onFocus={() => setListening(true)}
      onBlur={() => setListening(false)}
      onKeyDown={handleKeyDown}
    >
      {listening ? (
        <span className="text-muted-foreground animate-pulse">
          Press a key...
        </span>
      ) : value ? (
        <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-xs">
          {value}
        </kbd>
      ) : (
        <span className="text-muted-foreground">Click to record a key</span>
      )}
    </div>
  );
}

function PressStepEditor({
  step,
  onUpdate,
  inputVariableNames,
}: {
  step: any;
  onUpdate: (updates: any) => void;
  inputVariableNames: string[];
}) {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label>Key</Label>
        <KeyCaptureInput
          value={step.key || ""}
          onChange={(key) => onUpdate({ key })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label>Target Element</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Focus this element before pressing the key
            </p>
          </div>
          <Switch
            checked={!!step.selector}
            onCheckedChange={(checked) =>
              onUpdate({
                selector: checked
                  ? { primary: "", fallbacks: [], must_be_visible: true }
                  : null,
              })
            }
          />
        </div>
        {step.selector && (
          <SelectorEditor
            selector={step.selector}
            onChange={(selector) => onUpdate({ selector })}
            variableNames={inputVariableNames}
          />
        )}
      </div>
    </>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────

// ── Nested Step List (shared by Loop & If) ─────────────────────────

function NestedStepList({
  label,
  steps,
  onStepsChange,
  inputVariableNames,
  emptyMessage,
  helperText,
  insideLoop = false,
}: {
  label: string;
  steps: any[];
  onStepsChange: (steps: any[]) => void;
  inputVariableNames: string[];
  emptyMessage: string;
  helperText?: React.ReactNode;
  insideLoop?: boolean;
}) {
  const [isAddStepOpen, setIsAddStepOpen] = useState(false);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = steps.findIndex((s: any) => s.id === active.id);
      const newIndex = steps.findIndex((s: any) => s.id === over.id);
      onStepsChange(arrayMove(steps, oldIndex, newIndex));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <div className="space-y-2 rounded-lg border border-border p-3 bg-background">
        {steps.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">
            {emptyMessage}
          </p>
        ) : (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={steps.map((s: any) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {steps.map((innerStep: any, i: number) => {
                const priorOutputs: string[] = [];
                for (let j = 0; j < i; j++) {
                  priorOutputs.push(...collectStepOutputNames([steps[j]]));
                }
                return (
                  <StepCard
                    key={innerStep.id}
                    id={innerStep.id}
                    index={i}
                    step={innerStep}
                    onUpdate={(updates) =>
                      onStepsChange(
                        steps.map((s: any) =>
                          s.id === innerStep.id ? { ...s, ...updates } : s
                        )
                      )
                    }
                    onDelete={() =>
                      onStepsChange(
                        steps.filter((s: any) => s.id !== innerStep.id)
                      )
                    }
                    onReplace={(newStep) =>
                      onStepsChange(
                        steps.map((s: any) =>
                          s.id === innerStep.id
                            ? { ...newStep, id: innerStep.id }
                            : s
                        )
                      )
                    }
                    inputVariableNames={[
                      ...inputVariableNames,
                      ...priorOutputs,
                    ]}
                    insideLoop={insideLoop}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setIsAddStepOpen(true)}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Step
        </Button>
      </div>
      {helperText}

      <AddStepModal
        open={isAddStepOpen}
        onClose={() => setIsAddStepOpen(false)}
        onAdd={(newStep: any) => {
          onStepsChange([...steps, newStep]);
          setIsAddStepOpen(false);
        }}
        insideLoop={insideLoop}
      />
    </div>
  );
}

// ── Set Cookies Editor ─────────────────────────────────────────────

function SetCookiesEditor({
  step,
  onUpdate,
  inputVariableNames,
}: {
  step: any;
  onUpdate: (updates: any) => void;
  inputVariableNames: string[];
}) {
  const cookies: any[] = Array.isArray(step.cookies) ? step.cookies : [];

  const updateCookie = (index: number, patch: Record<string, unknown>) =>
    onUpdate({
      cookies: cookies.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });
  const removeCookie = (index: number) =>
    onUpdate({ cookies: cookies.filter((_, i) => i !== index) });
  const addCookie = () =>
    onUpdate({ cookies: [...cookies, { name: "", value: "" }] });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        {cookies.map((cookie, index) => (
          <div
            key={index}
            className="flex flex-col gap-1.5 rounded-md border border-border p-2"
          >
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <VariableInput
                  value={cookie.name || ""}
                  onChange={(name) => updateCookie(index, { name })}
                  placeholder="name"
                  variableNames={inputVariableNames}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeCookie(index)}
                aria-label="Remove cookie"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <VariableInput
              value={cookie.value || ""}
              onChange={(value) => updateCookie(index, { value })}
              placeholder="value (type / to insert variables)"
              variableNames={inputVariableNames}
            />
            <VariableInput
              value={cookie.domain || ""}
              onChange={(domain) => updateCookie(index, { domain })}
              placeholder="domain (optional: defaults to current page host)"
              variableNames={inputVariableNames}
            />
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={addCookie}>
        <Plus className="h-4 w-4 mr-1" />
        Add Cookie
      </Button>
      <div className="flex flex-col gap-1.5">
        <Label>Or Load From Variable (optional)</Label>
        <Input
          value={step.from_variable || ""}
          onChange={(e) => onUpdate({ from_variable: e.target.value })}
          placeholder="variable name from a prior Get Cookies step"
        />
      </div>
    </div>
  );
}

// ── Loop Step Editor ───────────────────────────────────────────────

function ItemKeysInput({
  value,
  onChange,
  placeholder = "Type a key name and press Enter",
}: {
  value: string[];
  onChange: (keys: string[]) => void;
  placeholder?: string;
}) {
  const [input, setInput] = useState("");

  const handleAdd = () => {
    const trimmed = input.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInput("");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleAdd}
          disabled={!input.trim()}
          className="shrink-0"
        >
          Add
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((key, i) => (
            <Badge
              key={key}
              variant="secondary"
              className="flex items-center gap-1 pr-1 font-mono"
            >
              {key}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="ml-0.5 hover:bg-muted rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

const SCRIPT_INPUT_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "json", label: "JSON" },
] as const;

const MAX_SCRIPT_BYTES = 1_000_000;

interface ScriptInputRow {
  name: string;
  value: string;
  value_type: string;
}

function RunScriptStepEditor({
  step,
  onUpdate,
  inputVariableNames,
}: {
  step: any;
  onUpdate: (updates: any) => void;
  inputVariableNames: string[];
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputs: ScriptInputRow[] = Array.isArray(step.inputs)
    ? step.inputs
    : [];
  const outputs: string[] = Array.isArray(step.outputs) ? step.outputs : [];

  const updateInput = (index: number, patch: Partial<ScriptInputRow>) =>
    onUpdate({
      inputs: inputs.map((row, i) =>
        i === index ? { ...row, ...patch } : row
      ),
    });

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_SCRIPT_BYTES) {
      SpotToast.error("Script is too large (max 1 MB)");
      return;
    }
    onUpdate({ script: await file.text() });
  };

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <Label>Python script</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => fileInputRef.current?.click()}
          >
            <FileUp className="h-3.5 w-3.5" />
            Upload .py
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".py,text/x-python"
            className="hidden"
            data-testid="run-script-upload"
            onChange={handleUpload}
          />
        </div>
        <CodeEditorSurface
          value={step.script ?? ""}
          onChange={(script) => onUpdate({ script })}
          language="python"
          minRows={12}
          inputs={[]}
          placeholder={
            "page.goto(inputs['url'])\noutputs['title'] = page.title()"
          }
        />
        <p className="text-xs text-muted-foreground">
          Runs inside this playbook&apos;s live browser. Available names: page,
          context, browser, inputs, outputs. Set outputs[&apos;name&apos;] for
          each declared output, or define run(page, inputs) and return a dict.{" "}
          {"{{variable}}"} tokens in the code are not substituted; pass values
          through Inputs.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Inputs</Label>
        {inputs.map((row, index) => (
          <div key={index} className="flex items-start gap-2">
            <Input
              value={row.name ?? ""}
              onChange={(e) => updateInput(index, { name: e.target.value })}
              placeholder="name"
              aria-label="Input name"
              className="w-32 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <VariableInput
                value={
                  typeof row.value === "string"
                    ? row.value
                    : String(row.value ?? "")
                }
                onChange={(value) => updateInput(index, { value })}
                placeholder="Value or variable"
                variableNames={inputVariableNames}
              />
            </div>
            <Select
              value={row.value_type ?? "text"}
              onValueChange={(value_type) => updateInput(index, { value_type })}
            >
              <SelectTrigger className="w-28 shrink-0" aria-label="Input type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCRIPT_INPUT_TYPES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 shrink-0"
              aria-label="Remove input"
              onClick={() =>
                onUpdate({ inputs: inputs.filter((_, i) => i !== index) })
              }
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() =>
            onUpdate({
              inputs: [...inputs, { name: "", value: "", value_type: "text" }],
            })
          }
        >
          <Plus className="h-3.5 w-3.5" />
          Add input
        </Button>
        <p className="text-xs text-muted-foreground">
          Each input is available as inputs[&apos;name&apos;] in the script.
          Number, Boolean and JSON values are parsed before the script runs.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Output variables</Label>
        <ItemKeysInput
          value={outputs}
          onChange={(names) => onUpdate({ outputs: names })}
          placeholder="Type an output name and press Enter"
        />
        <p className="text-xs text-muted-foreground">
          Each name becomes an output of this node (a list when the step runs
          inside a loop). The script must set every declared output.
        </p>
      </div>
    </>
  );
}

function LoopStepEditor({
  step,
  onUpdate,
  inputVariableNames,
}: {
  step: any;
  onUpdate: (updates: any) => void;
  inputVariableNames: string[];
}) {
  const { v2NodeId, listInputVariableNames } = useContext(
    PlaybookEditorContext
  );
  const over = step.over === "list" ? "list" : "count";
  const itemKeys: string[] = Array.isArray(step.item_keys)
    ? step.item_keys
    : [];
  // Item keys only make sense when the bound list carries objects; resolve
  // the bound output's type from the upstream options the picker uses.
  const upstreamOptions = useUpstreamVariableOptions();
  const boundListOutput = useMemo(() => {
    if (typeof step.items !== "string") return undefined;
    const match = WHOLE_VAR_REF_PATTERN.exec(step.items);
    if (!match?.[1]) return undefined;
    const refId = normalizeVariableRefId(match[1]);
    return upstreamOptions
      .flatMap((node) => node.outputs)
      .find((output) => normalizeVariableRefId(output.value) === refId);
  }, [step.items, upstreamOptions]);
  const showItemKeys =
    !!v2NodeId &&
    over === "list" &&
    !!boundListOutput &&
    boundListOutput.isList &&
    normalizeDataType(boundListOutput.dataType) === "object";
  const loopVars = [
    ...inputVariableNames,
    "loop_index",
    ...(over === "list" ? ["loop_item"] : []),
    ...(showItemKeys ? itemKeys.map((key) => `loop_item.${key}`) : []),
  ];

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label>Iteration Type</Label>
        <Select
          value={over}
          onValueChange={(next) => {
            if (next === over) return;
            if (next === "list") {
              onUpdate({ over: "list", count: null, items: "" });
            } else {
              onUpdate({ over: "count", count: 5, items: null });
            }
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="count">Number of times</SelectItem>
            <SelectItem value="list">List values</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {over === "count" ? (
        <div className="flex flex-col gap-1.5">
          <Label>Iterations</Label>
          <VariableInput
            value={step.count == null ? "" : String(step.count)}
            onChange={(raw) => {
              // Empty → leave unset.  Integer → store as number.  Anything
              // else (a :var[...] ref or {{template}}) stays a string —
              // LoopAction.count accepts `int | str | None`.  The floor
              // lives in the numeric input (min), not here, so the bound
              // path and the typed path behave the same.
              if (raw === "") {
                onUpdate({ count: "" });
              } else if (/^-?\d+$/.test(raw)) {
                onUpdate({ count: parseInt(raw, 10) });
              } else {
                onUpdate({ count: raw });
              }
            }}
            placeholder="e.g. 5, or a variable"
            variableNames={inputVariableNames}
            dataType="number"
            min={1}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label>List</Label>
          {v2NodeId ? (
            <BindableInput
              nodeId={v2NodeId}
              dataType="str"
              isList
              variableOnly
              value={typeof step.items === "string" ? step.items : ""}
              onChange={(items) => onUpdate({ items })}
            />
          ) : (
            <VariableInput
              value={typeof step.items === "string" ? step.items : ""}
              onChange={(items) => onUpdate({ items })}
              placeholder="Select a list variable"
              variableNames={listInputVariableNames}
            />
          )}
          <p className="text-xs text-muted-foreground">
            {v2NodeId
              ? "Runs once per list value."
              : "Runs once per list value. In steps inside the loop, bind Loop Item and Loop Index from the variable picker to use the current value and its position."}
          </p>
          {showItemKeys && (
            <>
              <Label className="mt-2">Item keys</Label>
              <ItemKeysInput
                value={itemKeys}
                onChange={(item_keys) =>
                  onUpdate({ item_keys: item_keys.length ? item_keys : null })
                }
              />
              <p className="text-xs text-muted-foreground">
                Declare only the object keys the steps inside the loop will use.
                They appear in the variable picker as <code>Item · key</code>{" "}
                chips.
              </p>
            </>
          )}
        </div>
      )}

      <NestedStepList
        label="Steps"
        steps={step.steps || []}
        onStepsChange={(steps) => onUpdate({ steps })}
        inputVariableNames={loopVars}
        emptyMessage="No steps inside this loop yet."
        insideLoop
      />
    </>
  );
}

// ── Condition Editor (shared by If & Break) ────────────────────────

function ConditionEditor({
  condition,
  onChange,
  inputVariableNames,
}: {
  condition: any;
  onChange: (condition: any) => void;
  inputVariableNames: string[];
}) {
  const isElementCondition =
    condition.type === "element_exists" ||
    condition.type === "element_not_exists";
  const isVariableCondition =
    condition.type === "variable_equals" ||
    condition.type === "variable_not_equals" ||
    condition.type === "variable_contains";

  const updateCondition = (updates: Record<string, unknown>) => {
    onChange({ ...condition, ...updates });
  };

  const handleConditionTypeChange = (newType: string) => {
    if (newType === "element_exists" || newType === "element_not_exists") {
      onChange({
        type: newType,
        selector: { primary: "", fallbacks: [], must_be_visible: true },
      });
    } else {
      onChange({
        type: newType,
        variable: "",
        value: "",
      });
    }
  };

  return (
    <>
      {/* Condition type */}
      <div className="flex flex-col gap-1.5">
        <Label>Condition</Label>
        <Select
          value={condition.type}
          onValueChange={handleConditionTypeChange}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="element_exists">Element Exists</SelectItem>
            <SelectItem value="element_not_exists">
              Element Does Not Exist
            </SelectItem>
            <SelectItem value="variable_equals">Variable Equals</SelectItem>
            <SelectItem value="variable_not_equals">
              Variable Does Not Equal
            </SelectItem>
            <SelectItem value="variable_contains">Variable Contains</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Condition-specific fields */}
      {isElementCondition && (
        <div className="flex flex-col gap-1.5">
          <Label>Element Selector</Label>
          <SelectorEditor
            selector={
              condition.selector || {
                primary: "",
                fallbacks: [],
                must_be_visible: true,
              }
            }
            onChange={(selector) => updateCondition({ selector })}
            variableNames={inputVariableNames}
          />
        </div>
      )}

      {isVariableCondition && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>Variable</Label>
            <Select
              value={condition.variable || ""}
              onValueChange={(variable) => updateCondition({ variable })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a variable" />
              </SelectTrigger>
              <SelectContent>
                {inputVariableNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {templateVariableLabel(name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>
              {condition.type === "variable_contains"
                ? "Contains Value"
                : "Expected Value"}
            </Label>
            <VariableInput
              value={String(condition.value ?? "")}
              onChange={(value) => updateCondition({ value })}
              placeholder="Expected value"
              variableNames={inputVariableNames}
            />
          </div>
        </>
      )}
    </>
  );
}

// ── Break Step Editor ──────────────────────────────────────────────

function BreakStepEditor({
  step,
  onUpdate,
  inputVariableNames,
}: {
  step: any;
  onUpdate: (updates: any) => void;
  inputVariableNames: string[];
}) {
  const condition = step.condition || {
    type: "element_exists",
    selector: { primary: "", fallbacks: [], must_be_visible: true },
  };

  return (
    <ConditionEditor
      condition={condition}
      onChange={(newCondition) => onUpdate({ condition: newCondition })}
      inputVariableNames={inputVariableNames}
    />
  );
}

// ── If Step Editor ─────────────────────────────────────────────────

function IfStepEditor({
  step,
  onUpdate,
  inputVariableNames,
  insideLoop = false,
}: {
  step: any;
  onUpdate: (updates: any) => void;
  inputVariableNames: string[];
  insideLoop?: boolean;
}) {
  const condition = step.condition || { type: "element_exists" };
  const thenSteps: any[] = step.then || [];
  const otherwiseSteps: any[] = step.otherwise || [];

  return (
    <>
      <ConditionEditor
        condition={condition}
        onChange={(newCondition) => onUpdate({ condition: newCondition })}
        inputVariableNames={inputVariableNames}
      />

      {/* Then branch */}
      <NestedStepList
        label="Then (if condition is true)"
        steps={thenSteps}
        onStepsChange={(steps) => onUpdate({ then: steps })}
        inputVariableNames={inputVariableNames}
        emptyMessage="No steps for the 'then' branch."
        insideLoop={insideLoop}
      />

      {/* Otherwise branch */}
      <NestedStepList
        label="Otherwise (if condition is false)"
        steps={otherwiseSteps}
        onStepsChange={(steps) => onUpdate({ otherwise: steps })}
        inputVariableNames={inputVariableNames}
        emptyMessage="No steps for the 'otherwise' branch (optional)."
        insideLoop={insideLoop}
      />
    </>
  );
}
